import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { apiAxios as axios } from "../../utils/apiClient.js";
import {
  fetchUniqueIds,
  sendFCMNotification,
} from "../../Services/NotificationServices";

import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Helmet } from "react-helmet";
import { encryptData } from "../../utils/encryption";
import {
  FaStar,
  FaStarHalfAlt,
  FaRegStar,
  FaPhone,
  FaInfoCircle,
  FaBook,
  FaCheck,
  FaSearch,
  FaFilter,
  FaSortAmountDown,
} from "react-icons/fa";
import Lottie from "react-lottie";
import animationData from "../../Assets/animated-json/green_tick.json";
import { url } from "../../Constant/TextConstant";
import { useAuth } from "../../utils/AuthContext";
import Header from "../Header";
import { useUserLocation } from "../../utils/useUserLocation";
import "./BrowseService.css";
import Footer from "../Footer/Footer";

const BrowseServices = () => {
  // State management
  const [serviceFullDataList, setServiceFullDataList] = useState([]);
  const [filteredServiceList, setFilteredServiceList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedRating, setSelectedRating] = useState(0);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [showBookDialog, setShowBookDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceSelection, setServiceSelection] = useState({});
  const [selectedServices, setSelectedServices] = useState([]);
  const [mrp, setMrp] = useState("0.00");
  const [description, setDescription] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    rating: null,
    experience: null,
    priceRange: null,
  });
  const [sortOption, setSortOption] = useState("default");
  const [priceRange, setPriceRange] = useState([0, 10000]);

  // Router and auth hooks
  const { subcategory } = useParams();
  const { currentUser, currentUserName } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef();

  // Use the location from useUserLocation hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

  // Update localStorage whenever selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Handler for modal close with location update
  const handleModalClose = (shouldClose, newLocation = null) => {
    setShowModal(false);
    if (newLocation) {
      updateLocation(newLocation);
    }
  };

  const serviceSubCategory = useMemo(
    () => decodeURIComponent(subcategory || ""),
    [subcategory]
  );

  // Calculate max price for price range slider
  const maxPrice = useMemo(() => {
    if (!serviceFullDataList.length) return 10000;
    return Math.max(
      ...serviceFullDataList.flatMap((service) =>
        service.serviceSubCategoryDetails.map((item) => parseFloat(item.mrp))
      )
    );
  }, [serviceFullDataList]);

  // Update price range when max price changes
  useEffect(() => {
    setPriceRange([0, Math.min(maxPrice, 10000)]);
  }, [maxPrice]);

  // Filter and sort services whenever filters, sort option, or search query changes
  useEffect(() => {
    if (serviceFullDataList.length === 0) return;

    let result = [...serviceFullDataList];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((service) => {
        return (
          service.serviceDetails.providerMobile.toLowerCase().includes(query) ||
          service.serviceDetails.businessName.toLowerCase().includes(query) ||
          service.serviceDetails.fullName.toLowerCase().includes(query)
        );
      });
    }

    // Apply rating filter
    if (selectedFilters.rating !== null) {
      result = result.filter(
        (service) => service.rating >= selectedFilters.rating
      );
    }

    // Apply experience filter
    if (selectedFilters.experience !== null) {
      result = result.filter((service) => {
        const expYears = parseInt(
          service.serviceDetails.experience.split(" ")[0]
        );
        return expYears >= selectedFilters.experience;
      });
    }

    // Apply price range filter
    if (selectedFilters.priceRange !== null) {
      result = result.filter((service) => {
        return service.serviceSubCategoryDetails.some(
          (item) =>
            parseFloat(item.mrp) >= selectedFilters.priceRange[0] &&
            parseFloat(item.mrp) <= selectedFilters.priceRange[1]
        );
      });
    }

    // Apply sorting
    switch (sortOption) {
      case "rating-high":
        result.sort((a, b) => b.rating - a.rating);
        break;
      case "rating-low":
        result.sort((a, b) => a.rating - b.rating);
        break;
      case "price-high":
        result.sort((a, b) => {
          const maxA = Math.max(
            ...a.serviceSubCategoryDetails.map((item) => parseFloat(item.mrp))
          );
          const maxB = Math.max(
            ...b.serviceSubCategoryDetails.map((item) => parseFloat(item.mrp))
          );
          return maxB - maxA;
        });
        break;
      case "price-low":
        result.sort((a, b) => {
          const minA = Math.min(
            ...a.serviceSubCategoryDetails.map((item) => parseFloat(item.mrp))
          );
          const minB = Math.min(
            ...b.serviceSubCategoryDetails.map((item) => parseFloat(item.mrp))
          );
          return minA - minB;
        });
        break;
      case "experience-high":
        result.sort((a, b) => {
          const expA = parseInt(a.serviceDetails.experience.split(" ")[0]);
          const expB = parseInt(b.serviceDetails.experience.split(" ")[0]);
          return expB - expA;
        });
        break;
      default:
        // Default sorting (by proximity if location is available)
        if (currentLocation?.coordinates) {
          // In a real app, you would sort by actual distance
          // This is just a placeholder
          result.sort(() => Math.random() - 0.5);
        }
        break;
    }

    setFilteredServiceList(result);
  }, [
    serviceFullDataList,
    searchQuery,
    selectedFilters,
    sortOption,
    currentLocation,
  ]);

  // API call functions
  const getUsersRating = useCallback(async (mobile) => {
    try {
      const response = await axios.get(
        `${url.baseAPIUrl}/api/ServiceLikeReviewCall/GetServiceRatingPercentage?ProviderMobile=${mobile}`
      );
      return response.data / 20;
    } catch (error) {
      return 0.0;
    }
  }, []);

  const fetchReviews = useCallback(async (mobile) => {
    try {
      const response = await axios.get(
        `${url.baseAPIUrl}/api/ServiceLikeReviewCall/GetReview?ProviderMobile=${mobile}`
      );
      return response.data || [];
    } catch (error) {
      return [];
    }
  }, []);

  const fetchSubCategories = useCallback(async (mobile) => {
    try {
      const response = await axios.get(
        `${url.baseAPIUrl}/api/BindServiceProduct/GetServicesByMobile?ProviderMobile=${mobile}`
      );
      return response.data.map((item) => ({
        serviceName: item.ServiceName,
        mrp: item.Mrp,
        image1: item.Image1,
        image2: item.Image2,
        pricePerAssign: item.PricePerAssign,
      }));
    } catch (error) {
      return [];
    }
  }, []);

  const fetchFullServiceData = useCallback(
    async (serviceDetails) => {
      const filteredServiceDetails = serviceDetails.filter(
        (service) => service.providerMobile !== currentUser
      );

      // Use a Set to track unique provider mobiles to avoid duplicates
      const uniqueProviders = new Set();
      const uniqueServiceDetails = filteredServiceDetails.filter((service) => {
        if (!uniqueProviders.has(service.providerMobile)) {
          uniqueProviders.add(service.providerMobile);
          return true;
        }
        return false;
      });

      const promises = uniqueServiceDetails.map(async (service) => {
        const [rating, reviews, subCategories] = await Promise.all([
          getUsersRating(service.providerMobile),
          fetchReviews(service.providerMobile),
          fetchSubCategories(service.providerMobile),
        ]);

        return {
          serviceDetails: service,
          serviceSubCategoryDetails: subCategories,
          rating: rating,
          reviews: reviews,
        };
      });

      return Promise.all(promises);
    },
    [currentUser, getUsersRating, fetchReviews, fetchSubCategories]
  );

  // When fetching data, update both full and filtered lists
  const fetchServiceDetails = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      // Wait for location to load if it's still loading
      if (isLocationLoading && !currentLocation) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      const response = await axios.get(
        `${
          url.baseAPIUrl
        }/api/BindServiceProduct/GetProductDetails?subcategory=${serviceSubCategory}&userLatitude=${
          currentLocation?.coordinates?.latitude || 0
        }&userLongitude=${currentLocation?.coordinates?.longitude || 0}`
      );

      if (response.status === 200) {
        const fetchedServiceDetails = response.data.map((item) => ({
          providerMobile: item.providerMobile || item.ProviderMobile || "",
          fullName: item.fullName || item.FullName || "Unknown",
          businessName: item.businessName || item.BusinessName || "",
          address: item.address || item.Address || "Not Available",
          experience: item.experience || item.Experience || "0 years",
          allImages: item.allImages ? item.allImages.split(",") : [],
        }));

        if (fetchedServiceDetails.length === 0) {
          if (serviceFullDataList.length === 0) {
            setError("No services available for this category");
          }
          return;
        }

        const fullServiceData = await fetchFullServiceData(
          fetchedServiceDetails
        );

        if (fullServiceData.length === 0 && serviceFullDataList.length === 0) {
          setError("No services available for this category");
        } else {
          setServiceFullDataList(fullServiceData);
          setFilteredServiceList(fullServiceData);
        }
      }
    } catch (error) {
      if (serviceFullDataList.length === 0) {
        setError("Failed to load service details. Please try again later.");
      }
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, [
    isLoading,
    currentLocation,
    serviceSubCategory,
    serviceFullDataList.length,
    isLocationLoading,
    fetchFullServiceData,
  ]);

  // Reset filters when subcategory changes
  useEffect(() => {
    setSearchQuery("");
    setSelectedFilters({
      rating: null,
      experience: null,
      priceRange: null,
    });
    setSortOption("default");
  }, [serviceSubCategory]);

  // Filter handler functions
  const handleRatingFilter = (rating) => {
    setSelectedFilters((prev) => ({
      ...prev,
      rating: prev.rating === rating ? null : rating,
    }));
  };

  const handleExperienceFilter = (years) => {
    setSelectedFilters((prev) => ({
      ...prev,
      experience: prev.experience === years ? null : years,
    }));
  };

  const handlePriceRangeChange = (min, max) => {
    setSelectedFilters((prev) => ({
      ...prev,
      priceRange: [min, max],
    }));
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setSelectedFilters({
      rating: null,
      experience: null,
      priceRange: null,
    });
    setSortOption("default");
  };

  // Reset and fetch data when subcategory changes
  useEffect(() => {
    setServiceFullDataList([]);
    setIsInitialLoad(true);

    const loadData = async () => {
      try {
        await fetchServiceDetails();
      } catch (err) {
      }
    };

    loadData();
  }, [serviceSubCategory]);

  // Handle service selection and MRP calculation
  const handleServiceSelection = useCallback((serviceName, isSelected) => {
    setServiceSelection((prev) => ({
      ...prev,
      [serviceName]: isSelected,
    }));

    setSelectedServices((prev) =>
      isSelected
        ? [...prev, serviceName]
        : prev.filter((name) => name !== serviceName)
    );
  }, []);

  useEffect(() => {
    if (selectedService && selectedServices.length > 0) {
      const totalMRP = selectedServices.reduce((sum, name) => {
        const service = selectedService.serviceSubCategoryDetails.find(
          (s) => s.serviceName === name
        );
        return sum + (parseFloat(service?.mrp) || 0);
      }, 0);
      setMrp(totalMRP > 0 ? totalMRP.toFixed(2) : "0.00");
    } else {
      setMrp("0.00");
    }
  }, [selectedServices, selectedService]);

  // Booking and review functions
  const bookService = useCallback(async () => {
    if (!selectedService || selectedServices.length === 0) return;

    const request = {
      customerMobile: currentUser,
      customerName: currentUserName || "Guest",
      providerMobile: selectedService.serviceDetails.providerMobile,
      serviceNames: selectedServices.join(","),
      description: description || "No Description",
    };

    try {
      const response = await axios.post(
        `${url.baseAPIUrl}/api/ServiceBooking/BookService`,
        null,
        { params: request }
      );

      if (response.status === 200) {
        setShowBookDialog(false);
        setShowSuccessDialog(true);
          // Send notification to store
              const supportUniqueIds = await fetchUniqueIds("9523812734");
              if (supportUniqueIds) {
                await sendFCMNotification({
                  Tokens: supportUniqueIds,
                  Title: "New Service Booking",
                  Body: `A new service booking has been placed by ${
                    currentUserName || "Customer"
                  }`,
                  ScreenRoute: "/serviceBooking",
                });
              }
                 const uniqueIds = await fetchUniqueIds(
                   selectedService.serviceDetails.providerMobile
                 );
                    await sendFCMNotification({
                      Tokens: uniqueIds,
                      Title: "New Service Booking",
                      Body: `A new service booking has been placed by ${
                        currentUserName || "Customer"
                      }`,
                      ScreenRoute: "/serviceBooking",
                    });
              
      }
    } catch (error) {
      setError("Failed to book service. Please try again.");
    }
  }, [
    selectedService,
    selectedServices,
    description,
    currentUser,
    currentUserName,
  ]);

  const submitReview = useCallback(async () => {
    if (selectedRating === 0 || !selectedService) return;

    try {
      await axios.post(
        `${url.baseAPIUrl}/api/ServiceLikeReviewCall/SubmitReview`,
        null,
        {
          params: {
            rating: selectedRating,
            review: reviewText,
            providermobile: selectedService.serviceDetails.providerMobile,
            customermobile: currentUser,
            name: currentUserName,
          },
        }
      );
      setShowRatingDialog(false);
      setReviewText("");
      setSelectedRating(0);
    } catch (error) {
      setError("Failed to submit review. Please try again.");
    }
  }, [
    selectedRating,
    reviewText,
    selectedService,
    currentUser,
    currentUserName,
  ]);

  const handleServiceSelect = useCallback((serviceData) => {
    setSelectedService(serviceData);
    const initialSelection = {};
    serviceData.serviceSubCategoryDetails.forEach((service) => {
      initialSelection[service.serviceName] = false;
    });
    setServiceSelection(initialSelection);
    setSelectedServices([]);
    setMrp("0.00");
    setDescription("");
    setShowBookDialog(true);
  }, []);

  const phoneCall = useCallback((service) => {
    setSelectedService(service);
    setShowRatingDialog(true);
  }, []);

  // Helper functions
  const renderStars = useCallback((rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<FaStar key={i} color="#FFD700" />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<FaStarHalfAlt key={i} color="#FFD700" />);
      } else {
        stars.push(<FaRegStar key={i} color="#FFD700" />);
      }
    }

    return stars;
  }, []);

  const renderServiceCard = useCallback(
    (serviceData) => {
      if (!serviceData?.serviceDetails) return null;

      return (
        <div
          className="service-card"
          key={`${serviceData.serviceDetails.providerMobile}-${serviceData.serviceDetails.businessName}`}
        >
          <div className="service-card-header">
            <div className="service-provider-info">
              <h3 className="service-business-name">
                {serviceData.serviceDetails.businessName}
              </h3>
              <p className="service-provider-name">
                {serviceData.serviceDetails.fullName}
              </p>
            </div>
            <div className="service-rating-badge">
              <div className="rating-stars">
                {renderStars(serviceData.rating)}
              </div>
              <span className="rating-text">
                {serviceData.rating.toFixed(1)} ({serviceData.reviews.length})
              </span>
            </div>
          </div>

          <div className="service-card-body">
            <Carousel
              showThumbs={false}
              showStatus={false}
              infiniteLoop
              autoPlay
              interval={3000}
              className="service-carousel"
            >
              {serviceData.serviceDetails.allImages?.map((image, idx) => (
                <div key={idx} className="service-image-container">
                  <img
                    src={`${url.serviceImageStartingPath}${image}`}
                    alt={`Service ${idx + 1}`}
                    className="service-image"
                    onError={(e) => {
                      e.target.src = `${url.serviceImageStartingPath}/default-service.jpg`;
                    }}
                  />
                </div>
              ))}
            </Carousel>

            <div className="service-details">
              <div className="service-meta">
                <div className="service-experience">
                  <span className="meta-label">Experience:</span>
                  <span>{serviceData.serviceDetails.experience}</span>
                </div>
                <div className="service-address">
                  <span className="meta-label">Location:</span>
                  <span>{serviceData.serviceDetails.address}</span>
                </div>
              </div>

              <div className="service-tags-container">
                <h4 className="services-offered-title">Services Offered</h4>
                <div className="service-tags">
                  {serviceData.serviceSubCategoryDetails?.map(
                    (service, idx) => (
                      <div
                        key={idx}
                        className={`service-tag ${
                          service.pricePerAssign === "ON" ? "call-service" : ""
                        }`}
                        onClick={() =>
                          service.pricePerAssign === "ON"
                            ? phoneCall(serviceData)
                            : handleServiceSelect(serviceData)
                        }
                      >
                        <span className="service-name">
                          {service.serviceName}
                        </span>
                        {service.pricePerAssign === "ON" ? (
                          <span className="service-price">
                            <FaPhone /> Call for Price
                          </span>
                        ) : (
                          <span className="service-price">₹{service.mrp}</span>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="service-card-footer">
            <button
              className="service-action-btn check-in-btn"
              onClick={() =>
                navigate(
                  `/provider-details/${encryptData(
                    serviceData.serviceDetails.providerMobile
                  )}`,
                  {
                    state: {
                      providerMobile: encryptData(
                        serviceData.serviceDetails.providerMobile
                      ),
                      serviceProviderName: serviceData.serviceDetails.fullName,
                    },
                  }
                )
              }
            >
              <FaCheck /> View Profile
            </button>
            <button
              className="service-action-btn book-btn"
              onClick={() => handleServiceSelect(serviceData)}
            >
              <FaBook /> Book Now
            </button>
          </div>
        </div>
      );
    },
    [phoneCall, handleServiceSelect, navigate, renderStars]
  );

  // Handle location errors
  useEffect(() => {
    if (locationError) {
      setError(
        "Unable to get your location. Showing results for default location."
      );
    }
  }, [locationError]);

  return (
    <>
      <Helmet>
        <title>
          {serviceSubCategory
            ? `${serviceSubCategory} Services | NearMe`
            : "Browse Services | NearMe"}
        </title>
        <meta
          name="description"
          content={`Find and book ${
            serviceSubCategory || "professional"
          } services near you. Compare providers, read reviews, and get the best deals.`}
        />
        <meta
          name="keywords"
          content={`${
            serviceSubCategory || "services"
          }, local services, service providers, book services, ${
            serviceSubCategory || ""
          } near me`}
        />
        <meta
          property="og:title"
          content={`${serviceSubCategory || "Services"} | NearMe`}
        />
        <meta
          property="og:description"
          content={`Browse and book ${
            serviceSubCategory || "professional"
          } services in your area`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
      </Helmet>
      <Header
        userLocation={currentLocation} // Use currentLocation from hook
        setShowModal={setShowModal}
        setUserLocation={updateLocation} // Use updateLocation from hook
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />

      <div className="browse-services" ref={scrollRef}>
        <div className="browse-services-header">
          <h2 className="browse-services-title">
            {serviceSubCategory || "Services"}
          </h2>
          <p className="browse-services-subtitle">
            {currentLocation?.coordinates
              ? "Find the best service providers near you"
              : "Showing services based on default location"}
          </p>
        </div>

        {/* Search, Filter, and Sort Section */}
        <div className="browse-services-controls">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by provider mobile, business name, or provider name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-sort-buttons">
            <button
              className={`filter-button ${showFilters ? "active" : ""}`}
              onClick={() => {
                setShowFilters(!showFilters);
                setShowSortOptions(false);
              }}
            >
              <FaFilter /> Filters
              {Object.values(selectedFilters).some(
                (filter) => filter !== null
              ) && <span className="filter-badge"></span>}
            </button>

            <button
              className={`sort-button ${showSortOptions ? "active" : ""}`}
              onClick={() => {
                setShowSortOptions(!showSortOptions);
                setShowFilters(false);
              }}
            >
              <FaSortAmountDown /> Sort
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="filters-panel">
            <div className="filter-section">
              <h4>Rating</h4>
              <div className="rating-filters">
                {[4, 3, 2, 1].map((rating) => (
                  <button
                    key={rating}
                    className={`rating-filter-btn ${
                      selectedFilters.rating === rating ? "active" : ""
                    }`}
                    onClick={() => handleRatingFilter(rating)}
                  >
                    {renderStars(rating)}
                    <span>& Up</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-section">
              <h4>Experience</h4>
              <div className="experience-filters">
                {[5, 3, 1].map((years) => (
                  <button
                    key={years}
                    className={`experience-filter-btn ${
                      selectedFilters.experience === years ? "active" : ""
                    }`}
                    onClick={() => handleExperienceFilter(years)}
                  >
                    {years}+ Years
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-section">
              <h4>Price Range</h4>
              <div className="price-range-slider">
                <div className="slider-container">
                  <input
                    type="range"
                    min="0"
                    max={maxPrice}
                    value={selectedFilters.priceRange?.[0] || priceRange[0]}
                    onChange={(e) => {
                      const minVal = parseInt(e.target.value);
                      const maxVal =
                        selectedFilters.priceRange?.[1] || priceRange[1];
                      handlePriceRangeChange(Math.min(minVal, maxVal), maxVal);
                    }}
                    className="range-slider min-slider"
                  />
                  <input
                    type="range"
                    min="0"
                    max={maxPrice}
                    value={selectedFilters.priceRange?.[1] || priceRange[1]}
                    onChange={(e) => {
                      const maxVal = parseInt(e.target.value);
                      const minVal =
                        selectedFilters.priceRange?.[0] || priceRange[0];
                      handlePriceRangeChange(minVal, Math.max(maxVal, minVal));
                    }}
                    className="range-slider max-slider"
                  />
                </div>
                <div className="price-range-values">
                  <span>
                    ₹{selectedFilters.priceRange?.[0] || priceRange[0]}
                  </span>
                  <span>-</span>
                  <span>
                    ₹{selectedFilters.priceRange?.[1] || priceRange[1]}
                  </span>
                </div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="clear-filters" onClick={clearAllFilters}>
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Sort Options Panel */}
        {showSortOptions && (
          <div className="sort-options-panel">
            <div
              className={`sort-option ${
                sortOption === "default" ? "active" : ""
              }`}
              onClick={() => setSortOption("default")}
            >
              <span>Default</span>
              {sortOption === "default" && <FaCheck />}
            </div>
            <div
              className={`sort-option ${
                sortOption === "rating-high" ? "active" : ""
              }`}
              onClick={() => setSortOption("rating-high")}
            >
              <span>Rating: High to Low</span>
              {sortOption === "rating-high" && <FaCheck />}
            </div>
            <div
              className={`sort-option ${
                sortOption === "rating-low" ? "active" : ""
              }`}
              onClick={() => setSortOption("rating-low")}
            >
              <span>Rating: Low to High</span>
              {sortOption === "rating-low" && <FaCheck />}
            </div>
            <div
              className={`sort-option ${
                sortOption === "price-high" ? "active" : ""
              }`}
              onClick={() => setSortOption("price-high")}
            >
              <span>Price: High to Low</span>
              {sortOption === "price-high" && <FaCheck />}
            </div>
            <div
              className={`sort-option ${
                sortOption === "price-low" ? "active" : ""
              }`}
              onClick={() => setSortOption("price-low")}
            >
              <span>Price: Low to High</span>
              {sortOption === "price-low" && <FaCheck />}
            </div>
            <div
              className={`sort-option ${
                sortOption === "experience-high" ? "active" : ""
              }`}
              onClick={() => setSortOption("experience-high")}
            >
              <span>Experience: High to Low</span>
              {sortOption === "experience-high" && <FaCheck />}
            </div>
          </div>
        )}

        {/* Results count */}
        {!isInitialLoad && (
          <div className="results-count">
            Showing {filteredServiceList.length} of {serviceFullDataList.length}{" "}
            services
          </div>
        )}

        {error && (
          <div className="browse-services-error">
            <div className="error-message">{error}</div>
            <button className="error-close" onClick={() => setError(null)}>
              &times;
            </button>
          </div>
        )}

        {isInitialLoad ? (
          <div className="browse-services-loading">
            <div className="loading-spinner"></div>
            <p>Loading services...</p>
          </div>
        ) : filteredServiceList.length === 0 ? (
          <div className="browse-services-empty">
            <div className="empty-state-icon">
              <svg viewBox="0 0 24 24" width="48" height="48">
                <path
                  fill="#7f8c8d"
                  d="M19,13H5V11H19V13M12,5A2,2 0 0,1 14,7A2,2 0 0,1 12,9A2,2 0 0,1 10,7A2,2 0 0,1 12,5M12,15A2,2 0 0,1 14,17A2,2 0 0,1 12,19A2,2 0 0,1 10,17A2,2 0 0,1 12,15Z"
                />
              </svg>
            </div>
            <h3>No services match your criteria</h3>
            <p>Try adjusting your filters or search query</p>
            <button className="clear-filters" onClick={clearAllFilters}>
              Clear All Filters
            </button>
          </div>
        ) : (
          <div className="service-cards-list">
            {filteredServiceList.map(renderServiceCard)}
          </div>
        )}

        {/* Dialogs */}
        {showRatingDialog && (
          <RatingDialog
            selectedRating={selectedRating}
            setSelectedRating={setSelectedRating}
            reviewText={reviewText}
            setReviewText={setReviewText}
            setShowRatingDialog={setShowRatingDialog}
            submitReview={submitReview}
          />
        )}

        {showBookDialog && selectedService && (
          <BookServiceDialog
            selectedService={selectedService}
            serviceSelection={serviceSelection}
            handleServiceSelection={handleServiceSelection}
            mrp={mrp}
            description={description}
            setDescription={setDescription}
            setShowBookDialog={setShowBookDialog}
            bookService={bookService}
            selectedServices={selectedServices}
            renderStars={renderStars}
          />
        )}

        {showSuccessDialog && (
          <SuccessDialog
            selectedService={selectedService}
            setShowSuccessDialog={setShowSuccessDialog}
            animationData={animationData}
          />
        )}
      </div>
      <Footer />
    </>
  );
};

// Dialog components remain the same as in your original code
const RatingDialog = ({
  selectedRating,
  setSelectedRating,
  reviewText,
  setReviewText,
  setShowRatingDialog,
  submitReview,
}) => (
  <div className="service-dialog-overlay">
    <div className="service-rating-dialog">
      <div className="dialog-header">
        <h3>Rate This Service</h3>
        <button onClick={() => setShowRatingDialog(false)}>&times;</button>
      </div>
      <div className="dialog-body">
        <div className="rating-section">
          <p>How would you rate this service?</p>
          <div className="rating-stars">
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                onClick={() => setSelectedRating(i + 1)}
                className="rating-star"
              >
                {i < selectedRating ? (
                  <FaStar color="#FFD700" size={28} />
                ) : (
                  <FaRegStar color="#FFD700" size={28} />
                )}
              </span>
            ))}
          </div>
        </div>
        <div className="review-section">
          <label>Your Review (Optional)</label>
          <textarea
            placeholder="Share your experience with this service..."
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            className="review-textarea"
          />
        </div>
      </div>
      <div className="dialog-footer">
        <button
          className="dialog-btn cancel-btn"
          onClick={() => setShowRatingDialog(false)}
        >
          Cancel
        </button>
        <button
          className="dialog-btn submit-btn"
          onClick={submitReview}
          disabled={selectedRating === 0}
        >
          Submit Review
        </button>
      </div>
    </div>
  </div>
);
const BookServiceDialog = ({
  selectedService,
  serviceSelection,
  handleServiceSelection,
  mrp,
  description,
  setDescription,
  setShowBookDialog,
  bookService,
  selectedServices,
  renderStars,
}) => (
  <div className="service-dialog-overlay">
    <div className="service-book-dialog">
      <div className="dialog-header">
        <h3>Book Service</h3>
        <button onClick={() => setShowBookDialog(false)}>&times;</button>
      </div>
      <div className="dialog-body">
        <div className="provider-info">
          <h4 className="provider-name">
            {selectedService.serviceDetails.businessName}
          </h4>
          <p className="provider-rating">
            {renderStars(selectedService.rating)}
            <span>{selectedService.rating.toFixed(1)}</span>
          </p>
        </div>

        <div className="services-selection">
          <h5>Select Services:</h5>
          <div className="services-list">
            {selectedService.serviceSubCategoryDetails?.map((service, idx) => (
              <div key={idx} className="service-item">
                <label className="service-checkbox">
                  <input
                    type="checkbox"
                    checked={serviceSelection[service.serviceName] || false}
                    onChange={(e) =>
                      handleServiceSelection(
                        service.serviceName,
                        e.target.checked
                      )
                    }
                  />
                  <span className="custom-checkbox"></span>
                  <span className="service-name">{service.serviceName}</span>
                  <span className="service-price">₹{service.mrp}</span>
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="total-section">
          <span>Total Amount:</span>
          <span className="total-amount">₹{mrp}</span>
        </div>

        <div className="description-section">
          <label>Additional Details:</label>
          <textarea
            placeholder="Describe your requirements or any special instructions..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="description-textarea"
          />
        </div>
      </div>
      <div className="dialog-footer">
        <button
          className="dialog-btn cancel-btn"
          onClick={() => setShowBookDialog(false)}
        >
          Cancel
        </button>
        <button
          className="dialog-btn book-btn"
          onClick={bookService}
          disabled={selectedServices.length === 0}
        >
          Confirm Booking
        </button>
      </div>
    </div>
  </div>
);

const SuccessDialog = ({
  selectedService,
  setShowSuccessDialog,
  animationData,
}) => (
  <div className="service-dialog-overlay">
    <div className="service-success-dialog">
      <div className="dialog-content">
        <Lottie
          options={{
            animationData: animationData,
            loop: false,
          }}
          height={120}
          width={120}
        />
        <h3 className="success-title">Booking Confirmed!</h3>
        <p className="success-message">
          Your service request has been successfully recorded. Our service
          provider will contact you shortly.
        </p>
        <div className="provider-contact">
          <span>You can also reach them at:</span>
          <span className="contact-number">
            📞 {selectedService?.serviceDetails.providerMobile}
          </span>
        </div>
        <button
          className="success-btn"
          onClick={() => setShowSuccessDialog(false)}
        >
          Done
        </button>
      </div>
    </div>
  </div>
);

export default BrowseServices;
